import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { User } from '@microsoft/microsoft-graph-types-beta';
import { Store } from '@ngrx/store';
import { MicrosoftEvent } from '@octiga/microsoft-events/src/interfaces/meta.interface';
import {
    BehaviorSubject,
    combineLatest,
    delay,
    filter,
    first,
    map,
    Observable,
    skipUntil,
    skipWhile,
    Subscription,
    switchMap,
    take,
} from 'rxjs';
import { AlertItem } from './interfaces/alerts.interface';
import { CurrentPostureCounts } from './interfaces/baseline.interface';
import { AlertService } from './services/alerts/alert.service';
import { EventService } from './services/events/event.service';
import { UserService } from './services/graph/user.service';
import { BaselineService } from './services/sway/baseline/baselines.service';
import { BaselineCurrentPostureCountService } from './services/sway/baseline/current_posture_count/baseline-count.service';
import { CategoryDataService } from './services/sway/category/category.service';
import { DeviationService } from './services/sway/deviation/deviation.service';
import { TicketService } from './services/sway/ticket/ticket.service';
import { Countries } from './utils/ISO-3166-Countries';
import {
    TicketCount,
    Baseline,
    Category,
    CombinedDeviation,
    NgOctReportService,
    TopAlert,
    CategoryService,
} from 'projects/oct-report-lib/src/public-api';

function getCountryfromEvent(event: MicrosoftEvent<any, any>) {
    if (!!event.reputation && !!event.reputation?.country_code && event.reputation.country_code !== 'N/A') {
        return Countries.find((c) => c['alpha-2'] === event.reputation?.country_code)!.name;
    } else {
        return 'N/A';
    }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'angular-report';

    alerts$: Observable<AlertItem[]>;
    events$: Observable<MicrosoftEvent<any, any>[]>;
    ticketCount$: Observable<TicketCount[]>;
    baselines$: Observable<Baseline[]>;
    users$: Observable<User[]>;
    categories$: Observable<Category[]>;
    baselineCurrentPostureCount$: Observable<CurrentPostureCounts>;
    deviations$: Observable<CombinedDeviation[]>;
    loading$: Observable<boolean> | Store<boolean>;

    loaded$: { [key: string]: BehaviorSubject<any> } = {
        header: new BehaviorSubject(false),
        baselines: new BehaviorSubject(false),
        categories: new BehaviorSubject(false),
        top_alerts: new BehaviorSubject(false),
        baseline_deviations: new BehaviorSubject(false),
        all_baselines_current_posture_count: new BehaviorSubject(false),
        ticket_count: new BehaviorSubject(false),
        users: new BehaviorSubject(false),
    };

    public all_loaded$ = combineLatest([
        this.loaded$['header'],
        this.loaded$['categories'],
        this.loaded$['top_alerts'],
        this.loaded$['all_baselines_current_posture_count'],
        this.loaded$['baseline_deviations'],
        this.loaded$['ticket_count'],
        this.loaded$['users'],
    ]).pipe(
        delay(2000),
        map((all_loaded) => all_loaded.every((loaded) => loaded)),
    );

    subscriptions: Subscription[] = [];

    constructor(
        private route: ActivatedRoute,
        private reportService: NgOctReportService,
        private categoryService: CategoryService,
        private alertService: AlertService,
        private eventService: EventService,
        private baselineService: BaselineService,
        private userService: UserService,
        private categoryDataService: CategoryDataService,
        private deviationService: DeviationService,
        private baselineCurrentPostureCountService: BaselineCurrentPostureCountService,
        private TicketService: TicketService,
    ) {
        this.alerts$ = alertService.entities$;
        this.events$ = eventService.entities$;
        this.baselines$ = baselineService.entities$;
        this.users$ = userService.entities$;
        this.categories$ = categoryDataService.entities$;
        this.deviations$ = deviationService.entities$;
        this.baselineCurrentPostureCount$ = baselineCurrentPostureCountService.entities$.pipe(map((e) => e[0]));
        this.ticketCount$ = TicketService.entities$;

        this.loading$ = combineLatest([
            alertService.loading$,
            baselineService.loading$,
            baselineCurrentPostureCountService.loading$,
            categoryDataService.loading$,
            deviationService.loading$,
            eventService.loading$,
            TicketService.loading$,
            userService.loading$,
        ]).pipe(map((statuses) => statuses.some((loading) => loading)));
    }

    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter(
                    (params) =>
                        !!params['msp-id'] &&
                        !!params['tenant-id'] &&
                        !!params['name'] &&
                        !!params['start'] &&
                        !!params['end'],
                ),
            )
            .subscribe((params) => {
                const { ['msp-id']: msp, ['tenant-id']: tenant, name, start, end, icon } = params;
                this.dispatcher(msp, tenant, name, start, end, icon);
                this.createReport(msp, tenant);
            });
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
    }

    private dispatcher(msp: string, tenant: string, name: string, start: string, end: string, icon?: string) {
        this.reportService.setHeader({
            tenant: name,
            date: { start, end },
            icon,
        });
        this.loaded$['header'].next(true);
        this.userService.getWithQuery({ msp, tenant });
        this.categoryDataService.setHeaders({
            'msp-id': msp,
            'tenant-id': tenant,
        });
        this.categoryDataService.getAll();
        this.baselineService.getWithQuery({ msp, tenant });
        this.baselineCurrentPostureCountService.getWithQuery({ msp, tenant });
        this.alertService.getWithQuery({ msp, tenant, start, end });
        this.deviationService.getWithQuery({ msp, tenant, start, end });
        this.TicketService.getWithQuery({ msp, tenant, start, end });
    }

    private createReport(msp: string, tenant: string) {
        const sub1 = this.setGraphUsers();
        const sub2 = this.setCategories();
        const sub3 = this.setBaselines();
        const sub4 = this.setBaselineCurrentPostureCount();
        const sub5 = this.setTopAlerts(msp, tenant);
        const sub6 = this.setBaselineDeviations();
        const sub7 = this.setTicketCount();

        this.subscriptions = [sub1, sub2, sub3, sub4, sub5, sub6, sub7];
    }

    private setTopAlerts(msp: string, tenant: string) {
        const alertsReady$ = combineLatest([this.alertService.loaded$, this.alertService.loading$]).pipe(
            first(([loaded, loading]) => !loading && loaded),
        );

        return this.alerts$
            .pipe(
                skipUntil(alertsReady$),
                take(1),
                switchMap((alerts) => {
                    alerts = alerts.filter((a) => a.source_type === 'microsoft-event' && a.severity !== 'warning');
                    alerts.sort((a, b) => b.etl_timestamp!.localeCompare(a.etl_timestamp!));
                    const alert_ids = alerts.map((a) => a.source_id);

                    this.eventService.getWithQuery({ msp, tenant, ids: alert_ids });

                    return this.mspAlertsToEvents(alert_ids.length).pipe(
                        map((events) => {
                            return alerts.map((alert) => {
                                const event = events.find((event) => !!event && event.id === alert.source_id);
                                const country = getCountryfromEvent(event!);
                                return { ...alert, country };
                            });
                        }),
                    );
                }),
            )
            .subscribe((alerts) => {
                this.loaded$['top_alerts'].next(true);
                this.reportService.createTopAlerts(alerts as TopAlert[]);
            });
    }

    private mspAlertsToEvents(ids_length: number) {
        return this.events$.pipe(
            map((events) => events.filter((event) => event !== undefined)),
            skipWhile((events) => events.length !== ids_length), // wait till they're all loaded
        );
    }

    private setBaselines() {
        return combineLatest([this.baselines$, this.baselineService.loaded$, this.baselineService.loading$]).subscribe(
            ([baselines, loaded, loading]) => {
                if (loaded && !loading) {
                    this.reportService.setBaselines(baselines);
                    this.loaded$['baselines'].next(true);
                }
            },
        );
    }

    private setBaselineDeviations() {
        return combineLatest([
            this.deviations$,
            this.deviationService.loaded$,
            this.deviationService.loading$,
        ]).subscribe(([baseline_deviations, loaded, loading]) => {
            if (loaded && !loading) {
                this.reportService.getBaselineDeviations(baseline_deviations);
                this.loaded$['baseline_deviations'].next(true);
            }
        });
    }

    private setBaselineCurrentPostureCount() {
        return combineLatest([
            this.baselineCurrentPostureCount$,
            this.baselineCurrentPostureCountService.loaded$,
            this.baselineCurrentPostureCountService.loading$,
        ]).subscribe(([counts, loaded, loading]) => {
            if (loaded && !loading) {
                this.reportService.setAllBaselineCurrentPostureCount(counts);
                this.loaded$['all_baselines_current_posture_count'].next(true);
            }
        });
    }
    private setCategories() {
        return this.categories$.pipe(filter((res) => !!res && res.length > 0)).subscribe((categories) => {
            this.categoryService.setCategories(categories);
            this.loaded$['categories'].next(true);
        });
    }

    private setGraphUsers() {
        return this.users$.pipe(filter((users) => !!users && users.length > 0)).subscribe((graph_users) => {
            this.reportService.setGraphUsers(graph_users);
            this.loaded$['users'].next(true);
        });
    }

    private setTicketCount() {
        return combineLatest([this.ticketCount$, this.TicketService.loaded$, this.TicketService.loading$]).subscribe(
            ([counts, loaded, loading]) => {
                if (loaded && !loading) {
                    this.reportService.getTicketCount(counts);
                    this.loaded$['ticket_count'].next(true);
                }
            },
        );
    }
}
